import classNames from 'classnames';
import values from 'lodash/values';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import * as PaymentsConstants from '../../constants';
import * as D from '../../deps';
import * as ProtectedUtils from '../../protectedUtils';
import * as PaymentsSelectors from '../../selectors';
import * as RouteUtils from '../../../../lib/RouteUtils';

import * as TOSAgreementUtils from './utils';

import './TOSAgreement.scss';

export class TOSAgreement extends Component {
  static getCheckboxStateKey(checkboxId) {
    return `${checkboxId}CheckboxIsChecked`;
  }

  static propTypes = {
    intl: PropTypes.object.isRequired,
    tier: PropTypes.oneOf(values(PaymentsConstants.tiers)),
    discountType: PropTypes.oneOf(values(PaymentsConstants.discountTypes)),
    trialPrice: PropTypes.number,
    offerTrialType: PropTypes.oneOf(values(PaymentsConstants.OfferTrialTypes)),
    trialTimesRenewable: PropTypes.number,
    durationType: PropTypes.oneOf(values(PaymentsConstants.DurationTypes)),
    price: PropTypes.number,
    backingProductPrice: PropTypes.number,
    offerDurationTime: PropTypes.number,
    offerDurationType: PropTypes.oneOf(values(PaymentsConstants.DurationTypes)),
    zipCode: PropTypes.string,
    activeProductPrice: PropTypes.number,
    activeProductNextBillingDate: PropTypes.string,
    undiscountedPrice: PropTypes.number,
    isChecked: PropTypes.bool,
    onChange: PropTypes.func,
    isInExperiment: PropTypes.bool,
  };

  state = {
    primaryCheckboxIsChecked: false,
    secondaryCheckboxIsChecked: false,
  };

  onToggle = ({ value, isChecked, numCheckboxes }) => {
    const newState = this.state;
    newState[TOSAgreement.getCheckboxStateKey(value)] = isChecked;
    this.setState(newState);
    const allTermsChecked =
      newState.primaryCheckboxIsChecked &&
      (newState.secondaryCheckboxIsChecked || numCheckboxes === 1);
    this.props.onChange({ isChecked: allTermsChecked });
  };

  getAgreementMessages = () => {
    const {
      tier,
      discountType,
      trialPrice,
      offerTrialType,
      trialTimesRenewable,
      durationType,
      price,
      backingProductPrice,
      offerDurationTime,
      offerDurationType,
      zipCode,
      activeProductPrice,
      activeProductNextBillingDate,
      undiscountedPrice,
      intl,
      isInExperiment,
    } = this.props;
    return TOSAgreementUtils.getTOSAgreementMessages({
      tier,
      discountType,
      trialPrice,
      offerTrialType,
      trialTimesRenewable,
      durationType,
      price,
      backingProductPrice,
      offerDurationTime,
      offerDurationType,
      zipCode,
      activeProductPrice,
      activeProductNextBillingDate,
      undiscountedPrice,
      intl,
      isInExperiment,
    });
  };

  renderCheckbox = (checkboxId, numCheckboxes) => {
    if (!this.props.onChange) {
      return null;
    }

    return (
      <div className='TOSAgreement__agreeToTerms'>
        <D.Checkbox
          value={checkboxId}
          isChecked={this.state[TOSAgreement.getCheckboxStateKey(checkboxId)]}
          onChange={({ value, isChecked }) => this.onToggle({ value, isChecked, numCheckboxes })}
          qaTag={D.Constants.QATags.PSCA}
        />
      </div>
    );
  };

  renderTOSItem = (message, index, messages) => {
    const checkboxId = index === 0 ? 'primary' : 'secondary';

    return (
      <label className='TOSAgreement__label' key={index}>
        {this.renderCheckbox(checkboxId, messages.length)}
        <FormattedMessage {...message.message} values={message.values} />
      </label>
    );
  };

  render() {
    const classes = classNames('TOSAgreement', {
      'TOSAgreement--has-checkbox': !!this.props.onChange,
    });

    return <div className={classes}>{this.getAgreementMessages().map(this.renderTOSItem)}</div>;
  }
}

export function mapStateToProps(state, ownProps) {
  const { tier, discountType, durationType } = ownProps;
  let trialPrice;
  let offerTrialType;
  let trialTimesRenewable;
  let selectedDurationType;
  let price;
  let backingProductPrice;
  let offerDurationTime;
  let offerDurationType;
  let zipCode;
  let activeProductPrice;
  let activeProductNextBillingDate;
  let undiscountedPrice;

  if (tier) {
    trialPrice = PaymentsSelectors.createGetTrialPriceSelector(state)(tier, discountType);
    offerTrialType = PaymentsSelectors.createGetOfferTrialTypeSelector(state)(tier, discountType);
    trialTimesRenewable = PaymentsSelectors.createGetTrialTimesRenewableSelector(state)(
      tier,
      discountType,
    );
    selectedDurationType =
      durationType ||
      PaymentsSelectors.createGetUpgradeOfferDurationTypeSelector(state)(tier, discountType);
    price = PaymentsSelectors.createGetUpgradeOfferPriceSelector(state)(
      tier,
      discountType,
      selectedDurationType,
    );
    backingProductPrice = PaymentsSelectors.createGetBackingProductPriceSelector(state)(
      tier,
      discountType,
      selectedDurationType,
    );
    offerDurationTime = PaymentsSelectors.createGetOfferDurationTimeSelector(state)(
      tier,
      discountType,
    );
    offerDurationType = PaymentsSelectors.createGetOfferDurationTypeSelector(state)(
      tier,
      discountType,
    );
    activeProductPrice = PaymentsSelectors.getActiveProductPrice(state);
    activeProductNextBillingDate = PaymentsSelectors.getNextBillingDate(state);
    const productGroups = PaymentsSelectors.getOfferedProductGroups(state);

    // @todo [WEBCA-1032] - Use a selector instead of using a protectedUtil
    undiscountedPrice = ProtectedUtils.getUndiscountedPrice(
      productGroups,
      tier,
      selectedDurationType,
    );
  }

  const listener = D.Utils.Listener.getCurrentListenerFromState(state);
  if (listener) {
    zipCode = listener.get('zipCode');
  }

  const isListenerInWebPaymentPageUpdatesExperiment =
    D.Utils.Listener.isListenerInWebPaymentPageUpdatesExperiment(state);
  const isListenerInWebSwitchPlansModalExperiment =
    D.Utils.Listener.isListenerInWebSwitchPlansModalExperiment(state);
  const isOnUpgradeOrPaymentsPage = RouteUtils.isOnUpgradeOrPaymentsPage(state);
  const isInExperiment =
    (isListenerInWebPaymentPageUpdatesExperiment && isOnUpgradeOrPaymentsPage) ||
    (isListenerInWebSwitchPlansModalExperiment && !isOnUpgradeOrPaymentsPage);

  return {
    tier,
    discountType,
    trialPrice,
    offerTrialType,
    trialTimesRenewable,
    durationType: selectedDurationType,
    price,
    backingProductPrice,
    offerDurationTime,
    offerDurationType,
    zipCode,
    activeProductPrice,
    activeProductNextBillingDate,
    undiscountedPrice,
    isInExperiment,
  };
}

export default connect(mapStateToProps, null)(injectIntl(TOSAgreement));
